"use client"

import clsx from "clsx"
import React, { useCallback, useEffect, useRef } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseFormElementProps } from "../../utils"

/**
 * 여러 줄 텍스트 입력
 *
 * @description
 * 제어(value) / 비제어(defaultValue) 모두 지원하는 통합 텍스트 영역입니다.
 * autoSize를 켜면 내용에 따라 높이가 자동 조절됩니다.
 *
 * @ai-style 기본 → 고정 높이(rows), autoSize=true → 자동 확장(minRows~maxRows)
 *
 * @example
 * ```tsx
 * // 제어 컴포넌트 (가장 일반적)
 * <TTextarea value={text} onChange={setText} rows={4} placeholder="설명을 입력하세요" />
 *
 * // 자동 높이 조절
 * <TTextarea value={comment} onChange={setComment} autoSize={{ minRows: 2, maxRows: 8 }} placeholder="댓글 입력" />
 *
 * // 라벨 포함
 * <TTextarea value={desc} onChange={setDesc} label="설명" rows={4} />
 *
 * // 비제어 컴포넌트
 * <TTextarea defaultValue="초기 텍스트" rows={4} onChange={handleChange} />
 * ```
 */
interface TTextareaProps extends BaseFormElementProps {
	/** 현재 텍스트 값 (제어 컴포넌트). value가 있으면 제어 모드, 없으면 defaultValue로 비제어 모드 */
	value?: string
	/** 초기 텍스트 값 (비제어 컴포넌트). value와 함께 사용하지 마세요 */
	defaultValue?: string
	/** 텍스트 영역 상단에 표시할 라벨 */
	label?: React.ReactNode
	/** 입력 필드 name 속성 */
	name?: string
	/** 플레이스홀더 텍스트 */
	placeholder?: string
	/**
	 * 경고 상태 표시
	 * @ai-style true=노란색(warning, #ffc300) 테두리 강조
	 */
	warning?: boolean
	/**
	 * 표시할 행 수 (autoSize 미사용 시)
	 * @default 2
	 */
	rows?: number
	/**
	 * 자동 높이 조절. true이면 기본값(minRows: 1) 사용, 객체로 세밀 제어 가능
	 * @default false
	 * @example autoSize={true}
	 * @example autoSize={{ minRows: 2, maxRows: 8 }}
	 */
	autoSize?: boolean | { minRows?: number; maxRows?: number }
	/** 마운트 시 자동 포커스 */
	autoFocus?: boolean
	/**
	 * 자동완성 속성
	 * @default "off"
	 */
	autoComplete?: string
	/** 맞춤법 검사 활성화 */
	spellCheck?: boolean
	/**
	 * Enter 키 비활성화. 채팅 입력 등에서 전송과 줄바꿈을 분리할 때 사용
	 * @default false
	 */
	disabledEnterKey?: boolean
	/** 텍스트 영역 하단에 렌더링할 내부 콘텐츠 (버튼 등) */
	renderInnerContent?: React.ReactNode
	/** 값 변경 콜백. 순수 문자열 전달 (이벤트 객체가 아님) */
	onChange?: (value: string) => void
	/** 클릭 콜백 */
	onClick?: () => void
	/** 포커스 콜백 */
	onFocus?: () => void
	/** 포커스 해제 콜백 */
	onBlur?: () => void
	/** 텍스트 선택 콜백 */
	onSelect?: () => void
	/** Enter 키 입력 콜백 (disabledEnterKey가 false일 때 동작) */
	onEnterKey?: (value: string) => void
}

export const TTextarea = React.memo(
	React.forwardRef<HTMLTextAreaElement, TTextareaProps>((props, ref) => {
		const {
			id,
			className,
			value,
			defaultValue,
			label,
			name,
			placeholder,
			required,
			disabled,
			readOnly,
			error,
			warning,
			rows = 2,
			autoSize = false,
			autoFocus,
			autoComplete = "off",
			spellCheck,
			disabledEnterKey = false,
			renderInnerContent,
			dataAttributes,
			onChange,
			onClick,
			onFocus,
			onBlur,
			onSelect,
			onEnterKey
		} = props

		const isControlled = value !== undefined
		const isAutoSize = autoSize !== false

		if (process.env.NODE_ENV === "development" && isControlled && defaultValue !== undefined) {
			console.warn("TTextarea: value와 defaultValue를 동시에 사용할 수 없습니다. value(제어 모드)가 우선 적용됩니다.")
		}
		const autoSizeConfig = typeof autoSize === "object" ? autoSize : {}
		const minRows = autoSizeConfig.minRows ?? 1
		const maxRows = autoSizeConfig.maxRows

		const textareaRef = useRef<HTMLTextAreaElement | null>(null)
		const dataProps = useDataAttributes(dataAttributes)

		const mergeRefs = useCallback(
			(node: HTMLTextAreaElement | null) => {
				textareaRef.current = node
				if (typeof ref === "function") {
					ref(node)
				} else if (ref && typeof ref === "object") {
					;(ref as React.RefObject<HTMLTextAreaElement | null>).current = node
				}
			},
			[ref]
		)

		const adjustHeight = useCallback(() => {
			const textarea = textareaRef.current
			if (!textarea) return

			textarea.style.height = "auto"
			const style = window.getComputedStyle(textarea)
			const paddingTop = parseFloat(style.paddingTop)
			const paddingBottom = parseFloat(style.paddingBottom)
			const borderTopWidth = parseFloat(style.borderTopWidth)
			const borderBottomWidth = parseFloat(style.borderBottomWidth)
			const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5

			let newHeight = textarea.scrollHeight

			if (minRows) {
				const minContentHeight = minRows * lineHeight
				const minPixelHeight = minContentHeight + paddingTop + paddingBottom + borderTopWidth + borderBottomWidth
				newHeight = Math.max(newHeight, minPixelHeight)
			}

			if (maxRows) {
				const maxContentHeight = maxRows * lineHeight
				const maxPixelHeight = maxContentHeight + paddingTop + paddingBottom + borderTopWidth + borderBottomWidth

				if (newHeight > maxPixelHeight) {
					textarea.style.height = `${maxPixelHeight}px`
					textarea.style.overflowY = "auto"
				} else {
					textarea.style.height = `${newHeight}px`
					textarea.style.overflowY = "hidden"
				}
			} else {
				textarea.style.height = `${newHeight}px`
				textarea.style.overflowY = "hidden"
			}
		}, [minRows, maxRows])

		// value 변경 + 마운트 시 높이 조정 (하나의 effect로 통합)
		useEffect(() => {
			if (isAutoSize) {
				adjustHeight()
			}
		}, [value, isAutoSize, adjustHeight])

		const handleChange = useCallback(
			(event: React.ChangeEvent<HTMLTextAreaElement>) => {
				if (isAutoSize) {
					adjustHeight()
				}
				if (onChange) {
					onChange(event.currentTarget.value)
				}
			},
			[isAutoSize, adjustHeight, onChange]
		)

		const handleKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
				if (event.key === "Enter") {
					if (disabledEnterKey) {
						event.preventDefault()
					} else if (onEnterKey) {
						onEnterKey(event.currentTarget.value)
					}
				}
			},
			[disabledEnterKey, onEnterKey]
		)

		const innerContentPadding = renderInnerContent ? (isAutoSize ? "pb-6!" : "pb-10!") : undefined

		return (
			<div className={clsx("TTextarea", innerContentPadding, className)} data-disabled={disabled} data-readonly={readOnly} data-invalid={error} data-warning={warning}>
				{label && <div className="shrink-0 mb-2">{label}</div>}
				<textarea
					ref={isAutoSize ? mergeRefs : ref}
					id={id}
					name={name}
					{...(isControlled ? { value: value ?? "" } : { defaultValue: defaultValue ?? "" })}
					placeholder={placeholder}
					required={required}
					disabled={disabled}
					readOnly={readOnly}
					aria-invalid={error}
					aria-required={required}
					rows={isAutoSize ? minRows : rows}
					autoFocus={autoFocus}
					autoComplete={autoComplete}
					spellCheck={spellCheck}
					{...(isAutoSize && { style: { overflowY: "hidden" as const } })}
					onClick={onClick}
					onFocus={onFocus}
					onBlur={onBlur}
					onSelect={onSelect}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					{...dataProps}
				/>
				{renderInnerContent && <div className="inner">{renderInnerContent}</div>}
			</div>
		)
	})
)

TTextarea.displayName = "TTextarea"
